import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { OpeningTimes } from 'apis/restaurant-api/merchant-admin/venue/types';
import { fromWeeklyScheduleToTimePairsByDate } from 'common/components/weekly-schedules';
import { Typography as T } from 'common/typography';
import time from 'common/utils/time';
import { WEEKDAYS } from 'common/utils/time/values';

type Props = {
  className?: string;
  effectiveOpeningTimes: OpeningTimes;
  timezone: string;
};

// This must match Restaurant APIs day cut-off.
// Restaurant API returns effective opening times for the seven day period
// starting on the most recent 5am.
const CUT_OFF_HOUR = 5;

const Root = styled.div``;

const Title = styled(T.Body3)`
  display: block;
  margin-block-end: 16px;
  color: ${cbSemanticCssVars.textSecondary};
`;

const Rows = styled.div`
  margin-block-end: 24px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Times = styled(T.Body3).attrs({ fontWeight: 'semiBold' })`
  text-align: end;
`;

const weekdays = {
  monday: 'week.mon',
  tuesday: 'week.tue',
  wednesday: 'week.wed',
  thursday: 'week.thu',
  friday: 'week.fri',
  saturday: 'week.sat',
  sunday: 'week.sun',
};

const EffectiveOpeningTimes = ({ className, effectiveOpeningTimes, timezone }: Props) => {
  const [now] = useState(Date.now());
  const timePairsByDate = useMemo(
    () => fromWeeklyScheduleToTimePairsByDate(CUT_OFF_HOUR, timezone, now, effectiveOpeningTimes),
    [effectiveOpeningTimes, timezone, now],
  );
  return (
    <Root className={className}>
      <Title>
        <FormattedMessage id="venue-details.effective-opening-times-title" />
      </Title>
      <Rows>
        {timePairsByDate.map(({ date: dateString, timePairs }) => {
          const date = DateTime.fromISO(dateString, { zone: timezone });
          const weekday = WEEKDAYS[date.weekday - 1];
          return (
            <Row key={dateString}>
              <T.Body3>
                <FormattedMessage id={weekdays[weekday]} /> {date.toLocaleString({ month: 'numeric', day: 'numeric' })}
              </T.Body3>
              <Times>
                {timePairs.length > 0 ? (
                  timePairs.map((hours, i) => (
                    <div key={i}>
                      {time.renderMinutesFromMidnight(hours.start, false)}–
                      {time.renderMinutesFromMidnight(hours.end, true)}
                    </div>
                  ))
                ) : (
                  <FormattedMessage id="opening-hours.closed" />
                )}
              </Times>
            </Row>
          );
        })}
      </Rows>
      <InputAlert variant="info">
        <FormattedMessage id="venue-details.effective-opening-times-info" />
      </InputAlert>
    </Root>
  );
};

export default EffectiveOpeningTimes;
